import { useState, useMemo } from 'react'
import { X, Copy, Phone, Mail, MapPin, Truck, CreditCard, Package, ChevronDown, Loader2, Check, MessageSquare, FileText, Building2, User, Warehouse, Plus, Trash2, RotateCcw, Pencil, Link2 } from 'lucide-react'
import { ShippingOrder, shipping, ShipmentProduct } from '@/lib/api'
import { CountryFlag } from '@/components/CarrierLogos'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import AddProductModal from './AddProductModal'

interface OrderDetailModalProps {
  order: ShippingOrder
  onClose: () => void
  onSave: (data: {
    name: string
    company: string
    email: string
    phone: string
    street: string
    street2: string
    city: string
    postalCode: string
    country: string
    nif: string
  }) => Promise<void>
  onAddToQueue?: (orderId: number) => void
}

export default function OrderDetailModal({ order, onClose, onSave, onAddToQueue }: OrderDetailModalProps) {
  const [isLoading, setIsLoading] = useState(false)
  const [isAddingToQueue, setIsAddingToQueue] = useState(false)
  const [isEditing, setIsEditing] = useState(false)
  const [showBillingAddress, setShowBillingAddress] = useState(false)
  const [copiedField, setCopiedField] = useState<string | null>(null)
  const [showAddProductModal, setShowAddProductModal] = useState(false)
  const [editingProduct, setEditingProduct] = useState<ShipmentProduct | null>(null)

  const queryClient = useQueryClient()

  // Obtener modificaciones de productos del envío
  const { data: productModifications } = useQuery({
    queryKey: ['shipment-products', order.id],
    queryFn: () => shipping.getShipmentProducts(order.id)
  })

  // Mutations para gestionar productos
  const markRemovedMutation = useMutation({
    mutationFn: (productId: number) => shipping.markProductRemoved(order.id, productId),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['shipment-products', order.id] })
  })

  const restoreProductMutation = useMutation({
    mutationFn: (productId: number) => shipping.restoreProduct(order.id, productId),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['shipment-products', order.id] })
  })

  const deleteCustomProductMutation = useMutation({
    mutationFn: (productId: number) => shipping.deleteShipmentProduct(order.id, productId),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['shipment-products', order.id] })
  })

  const updateQuantityMutation = useMutation({
    mutationFn: ({ productId, quantity }: { productId: number; quantity: number }) =>
      shipping.updateProductQuantity(order.id, productId, quantity),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['shipment-products', order.id] })
  })

  // Combinar productos de PrestaShop con modificaciones locales
  const combinedProducts = useMemo(() => {
    const mods = productModifications?.modifications || []
    const removedIds = new Set(mods.filter(m => m.is_removed).map(m => m.original_product_id))
    const quantityMods = new Map(mods.filter(m => !m.is_custom && !m.is_removed && m.original_product_id).map(m => [m.original_product_id, m.quantity]))
    const customProducts = mods.filter(m => m.is_custom && !m.is_removed)

    // Productos de PrestaShop (filtrados y con cantidades modificadas)
    const psProducts = (order.products || [])
      .filter(p => !removedIds.has(p.id))
      .map(p => ({
        ...p,
        quantity: quantityMods.get(p.id) ?? p.quantity,
        isFromPrestaShop: true as const,
        isRemoved: false
      }))

    // Productos personalizados añadidos
    const custom = customProducts.map(p => ({
      id: p.id,
      name: p.product_name,
      reference: p.product_reference,
      quantity: p.quantity,
      price: p.price,
      total: p.price * p.quantity,
      image_url: p.image_url,
      isFromPrestaShop: false as const,
      isRemoved: false,
      shipmentProductId: p.id
    }))

    return [...psProducts, ...custom]
  }, [order.products, productModifications])

  // Productos eliminados (para mostrar opción de restaurar)
  const removedProducts = useMemo(() => {
    const mods = productModifications?.modifications || []
    const removedMods = mods.filter(m => m.is_removed && m.original_product_id)
    return (order.products || []).filter(p => removedMods.some(m => m.original_product_id === p.id))
  }, [order.products, productModifications])

  // Datos editables - se inicializan directamente desde order
  const [deliveryData, setDeliveryData] = useState({
    name: order.shipping_address.name,
    company: order.shipping_address.company || '',
    email: order.customer_email,
    phone: order.shipping_address.phone_mobile || order.shipping_address.phone || '',
    street: order.shipping_address.street,
    street2: order.shipping_address.street2 || '',
    city: order.shipping_address.city,
    postalCode: order.shipping_address.postal_code,
    country: order.shipping_address.country_code,
    nif: order.dni || '',
  })

  // Detectar si hay cambios comparando directamente con order
  const hasChanges = useMemo(() => {
    const original = {
      name: order.shipping_address.name,
      company: order.shipping_address.company || '',
      email: order.customer_email,
      phone: order.shipping_address.phone_mobile || order.shipping_address.phone || '',
      street: order.shipping_address.street,
      street2: order.shipping_address.street2 || '',
      city: order.shipping_address.city,
      postalCode: order.shipping_address.postal_code,
      country: order.shipping_address.country_code,
      nif: order.dni || '',
    }
    return Object.keys(original).some(
      key => deliveryData[key as keyof typeof deliveryData] !== original[key as keyof typeof original]
    )
  }, [deliveryData, order])

  const handleSave = async () => {
    setIsLoading(true)
    try {
      await onSave(deliveryData)
    } finally {
      setIsLoading(false)
    }
  }

  const copyToClipboard = (text: string, field: string) => {
    navigator.clipboard.writeText(text)
    setCopiedField(field)
    setTimeout(() => setCopiedField(null), 2000)
  }

  const formatDate = (dateStr: string) => {
    const date = new Date(dateStr)
    return {
      time: date.toLocaleTimeString('es-ES', { hour: '2-digit', minute: '2-digit' }),
      date: date.toLocaleDateString('es-ES', { day: '2-digit', month: '2-digit', year: 'numeric' })
    }
  }

  const { time, date } = formatDate(order.date_add)

  return (
    <div className="fixed inset-0 bg-black/50 flex items-start justify-center pt-6 z-50 overflow-y-auto" onClick={onClose}>
      <div className="bg-white dark:bg-slate-800 rounded-xl shadow-2xl w-full max-w-6xl my-6 overflow-hidden" onClick={e => e.stopPropagation()}>

        {/* Header - Barra horizontal con info del pedido (estilo Outvio) */}
        <div className="bg-slate-50 dark:bg-slate-900/50 border-b border-slate-200 dark:border-slate-700">
          {/* Columnas del header */}
          <div className="grid grid-cols-[60px_100px_120px_80px_80px_60px_100px_40px] gap-3 px-4 py-2 text-[10px] font-medium text-teal-600 dark:text-teal-400 uppercase tracking-wider border-b border-slate-200 dark:border-slate-700">
            <div>Fuente</div>
            <div>Fecha</div>
            <div>ID del pedido</div>
            <div>Destino</div>
            <div>Total</div>
            <div>Productos</div>
            <div>Estado</div>
            <div></div>
          </div>

          {/* Valores */}
          <div className="grid grid-cols-[60px_100px_120px_80px_80px_60px_100px_40px] gap-3 px-4 py-3 items-center text-sm">
            {/* Fuente - Logo PrestaShop */}
            <div>
              <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-pink-500 to-purple-600 flex items-center justify-center shadow-sm">
                <span className="text-white font-bold text-[10px]">PS</span>
              </div>
            </div>

            {/* Fecha */}
            <div className="text-slate-600 dark:text-slate-400">
              <div className="font-medium">{time}</div>
              <div className="text-xs">{date}</div>
            </div>

            {/* ID Pedido */}
            <div>
              <div className="font-semibold text-slate-900 dark:text-white">{order.id}</div>
              <div className="text-xs text-slate-500">{order.reference}</div>
            </div>

            {/* Destino */}
            <div className="flex items-center gap-1.5">
              <CountryFlag countryCode={order.shipping_address.country_code} className="w-5 h-4" />
              <span className="text-slate-600 dark:text-slate-400 text-xs">{order.shipping_address.country_code}</span>
            </div>

            {/* Total */}
            <div className="font-semibold text-slate-900 dark:text-white">
              €{order.total.toFixed(2)}
            </div>

            {/* Productos */}
            <div className="text-center text-slate-600 dark:text-slate-400">
              {order.items_count}
            </div>

            {/* Estado */}
            <div>
              <span className="px-2 py-1 text-[10px] font-semibold rounded bg-amber-100 text-amber-700 dark:bg-amber-900/50 dark:text-amber-400">
                {order.state_name || 'PENDIENTE'}
              </span>
            </div>

            {/* Cerrar */}
            <div className="flex justify-end">
              <button
                onClick={onClose}
                className="p-1.5 text-slate-400 hover:text-slate-600 dark:hover:text-slate-300 hover:bg-slate-200 dark:hover:bg-slate-700 rounded-lg transition-colors"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
          </div>
        </div>

        {/* Contenido principal - 3 columnas */}
        <div className="flex">
          {/* Columna 1: Datos de Entrega */}
          <div className="flex-1 p-5 border-r border-slate-200 dark:border-slate-700">
            <h3 className="text-xs font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wider mb-4">
              Datos de Entrega
            </h3>

            <div className="space-y-3">
              {/* Nombre */}
              <div className="flex items-center gap-3">
                <User className="w-4 h-4 text-slate-400 flex-shrink-0" />
                <input
                  type="text"
                  value={deliveryData.name}
                  onChange={(e) => setDeliveryData({ ...deliveryData, name: e.target.value })}
                  disabled={!isEditing}
                  className={`flex-1 px-3 py-2 text-sm rounded-lg border transition-colors ${
                    isEditing
                      ? 'border-slate-200 dark:border-slate-600 bg-white dark:bg-slate-700 focus:ring-2 focus:ring-teal-500 focus:border-teal-500'
                      : 'border-transparent bg-transparent cursor-default'
                  }`}
                />
                <button
                  onClick={() => copyToClipboard(deliveryData.name, 'name')}
                  className="p-1.5 text-slate-400 hover:text-teal-600 rounded transition-colors"
                >
                  {copiedField === 'name' ? <Check className="w-4 h-4 text-green-500" /> : <Copy className="w-4 h-4" />}
                </button>
              </div>

              {/* NIF */}
              <div className="flex items-center gap-3">
                <FileText className="w-4 h-4 text-slate-400 flex-shrink-0" />
                <input
                  type="text"
                  value={deliveryData.nif}
                  onChange={(e) => setDeliveryData({ ...deliveryData, nif: e.target.value })}
                  disabled={!isEditing}
                  placeholder="NIF destinatario"
                  className={`flex-1 px-3 py-2 text-sm rounded-lg border transition-colors ${
                    isEditing
                      ? 'border-slate-200 dark:border-slate-600 bg-white dark:bg-slate-700 focus:ring-2 focus:ring-teal-500 focus:border-teal-500 placeholder:text-slate-400'
                      : 'border-transparent bg-transparent cursor-default placeholder:text-transparent'
                  }`}
                />
              </div>

              {/* Email */}
              <div className="flex items-center gap-3">
                <Mail className="w-4 h-4 text-slate-400 flex-shrink-0" />
                <input
                  type="email"
                  value={deliveryData.email}
                  onChange={(e) => setDeliveryData({ ...deliveryData, email: e.target.value })}
                  disabled={!isEditing}
                  className={`flex-1 px-3 py-2 text-sm rounded-lg border transition-colors ${
                    isEditing
                      ? 'border-slate-200 dark:border-slate-600 bg-white dark:bg-slate-700 focus:ring-2 focus:ring-teal-500 focus:border-teal-500'
                      : 'border-transparent bg-transparent cursor-default'
                  }`}
                />
              </div>

              {/* Teléfono */}
              <div className="flex items-center gap-3">
                <Phone className="w-4 h-4 text-slate-400 flex-shrink-0" />
                <input
                  type="tel"
                  value={deliveryData.phone}
                  onChange={(e) => setDeliveryData({ ...deliveryData, phone: e.target.value })}
                  disabled={!isEditing}
                  placeholder="Teléfono"
                  className={`flex-1 px-3 py-2 text-sm rounded-lg border transition-colors ${
                    isEditing
                      ? 'border-slate-200 dark:border-slate-600 bg-white dark:bg-slate-700 focus:ring-2 focus:ring-teal-500 focus:border-teal-500'
                      : 'border-transparent bg-transparent cursor-default placeholder:text-transparent'
                  }`}
                />
              </div>

              {/* Empresa */}
              <div className="flex items-center gap-3">
                <Building2 className="w-4 h-4 text-slate-400 flex-shrink-0" />
                <input
                  type="text"
                  value={deliveryData.company}
                  onChange={(e) => setDeliveryData({ ...deliveryData, company: e.target.value })}
                  disabled={!isEditing}
                  placeholder="Empresa"
                  className={`flex-1 px-3 py-2 text-sm rounded-lg border transition-colors ${
                    isEditing
                      ? 'border-slate-200 dark:border-slate-600 bg-white dark:bg-slate-700 focus:ring-2 focus:ring-teal-500 focus:border-teal-500'
                      : 'border-transparent bg-transparent cursor-default placeholder:text-transparent'
                  }`}
                />
              </div>

              {/* Mensaje al transportista (solo lectura) */}
              {order.gift_message && (
                <div className="flex items-start gap-3">
                  <MessageSquare className="w-4 h-4 text-slate-400 flex-shrink-0 mt-1" />
                  <div className="flex-1">
                    <p className="text-sm text-slate-700 dark:text-slate-300">{order.gift_message}</p>
                    <p className="text-xs text-slate-400 mt-1">Editable desde la cola de envío</p>
                  </div>
                </div>
              )}

              {/* Toggle dirección facturación */}
              <button
                onClick={() => setShowBillingAddress(!showBillingAddress)}
                className="text-sm text-teal-600 dark:text-teal-400 hover:underline flex items-center gap-1 mt-2"
              >
                <ChevronDown className={`w-4 h-4 transition-transform ${showBillingAddress ? 'rotate-180' : ''}`} />
                Cambiar dirección de facturación
              </button>

              {showBillingAddress && (
                <div className="mt-2 p-3 bg-slate-50 dark:bg-slate-900/50 rounded-lg text-sm text-slate-600 dark:text-slate-400">
                  Misma que dirección de envío
                </div>
              )}
            </div>
          </div>

          {/* Columna 2: Dirección + Transportista */}
          <div className="flex-1 p-5 border-r border-slate-200 dark:border-slate-700 space-y-6">
            {/* Dirección */}
            <div>
              <h3 className="text-xs font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wider mb-4">
                Dirección de Envío
              </h3>

              <div className="space-y-3">
                {/* Calle */}
                <div className="flex items-center gap-3">
                  <MapPin className="w-4 h-4 text-slate-400 flex-shrink-0" />
                  <input
                    type="text"
                    value={deliveryData.street}
                    onChange={(e) => setDeliveryData({ ...deliveryData, street: e.target.value })}
                    disabled={!isEditing}
                    className={`flex-1 px-3 py-2 text-sm rounded-lg border transition-colors ${
                      isEditing
                        ? 'border-slate-200 dark:border-slate-600 bg-white dark:bg-slate-700 focus:ring-2 focus:ring-teal-500 focus:border-teal-500'
                        : 'border-transparent bg-transparent cursor-default'
                    }`}
                  />
                </div>

                {/* Ciudad */}
                <div className="flex items-center gap-3 pl-7">
                  <input
                    type="text"
                    value={deliveryData.city}
                    onChange={(e) => setDeliveryData({ ...deliveryData, city: e.target.value })}
                    disabled={!isEditing}
                    placeholder="Ciudad"
                    className={`flex-1 px-3 py-2 text-sm rounded-lg border transition-colors ${
                      isEditing
                        ? 'border-slate-200 dark:border-slate-600 bg-white dark:bg-slate-700 focus:ring-2 focus:ring-teal-500 focus:border-teal-500'
                        : 'border-transparent bg-transparent cursor-default placeholder:text-transparent'
                    }`}
                  />
                </div>

                {/* CP + País */}
                <div className="flex items-center gap-3 pl-7">
                  <input
                    type="text"
                    value={deliveryData.postalCode}
                    onChange={(e) => setDeliveryData({ ...deliveryData, postalCode: e.target.value })}
                    disabled={!isEditing}
                    placeholder="C.P."
                    className={`w-24 px-3 py-2 text-sm rounded-lg border transition-colors ${
                      isEditing
                        ? 'border-slate-200 dark:border-slate-600 bg-white dark:bg-slate-700 focus:ring-2 focus:ring-teal-500 focus:border-teal-500'
                        : 'border-transparent bg-transparent cursor-default'
                    }`}
                  />
                  <div className="flex items-center gap-2 flex-1 px-3 py-2 rounded-lg border border-slate-200 dark:border-slate-600 bg-slate-50 dark:bg-slate-800">
                    <CountryFlag countryCode={deliveryData.country} className="w-5 h-4" />
                    <span className="text-sm text-slate-700 dark:text-slate-300">
                      {deliveryData.country === 'ES' ? 'España' : deliveryData.country === 'PT' ? 'Portugal' : deliveryData.country}
                    </span>
                  </div>
                </div>
              </div>
            </div>

            {/* Service Point DHL */}
            {order.service_point && (
              <div className="rounded-lg border-l-4 border-yellow-400 bg-yellow-50 dark:bg-yellow-900/20 p-4">
                <div className="flex items-center gap-2 mb-2">
                  <MapPin className="w-4 h-4 text-yellow-600 dark:text-yellow-400" />
                  <span className="text-xs font-semibold text-yellow-700 dark:text-yellow-300 uppercase tracking-wider">Punto de recogida DHL</span>
                </div>
                <p className="text-sm font-medium text-slate-900 dark:text-white">{order.service_point.name}</p>
                <p className="text-sm text-slate-600 dark:text-slate-300">{order.service_point.address}</p>
                <p className="text-sm text-slate-600 dark:text-slate-300">{order.service_point.postal_code} {order.service_point.city} ({order.service_point.country})</p>
              </div>
            )}

            {/* Transportista */}
            <div>
              <h3 className="text-xs font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wider mb-4">
                Transportista
              </h3>

              <div className="flex items-center gap-3">
                <Truck className="w-4 h-4 text-slate-400 flex-shrink-0" />
                <div className="flex-1 px-3 py-2 rounded-lg border border-slate-200 dark:border-slate-600 bg-slate-50 dark:bg-slate-800">
                  <p className="text-sm font-medium text-slate-900 dark:text-white">{order.carrier_name}</p>
                  {order.suggested_carrier && (
                    <p className="text-xs text-slate-500 dark:text-slate-400 mt-0.5">
                      Sugerido: <span className="text-teal-600 dark:text-teal-400">{order.suggested_carrier.carrier_code.toUpperCase()}</span>
                      {order.suggested_carrier.rule_matched && (
                        <span> • {order.suggested_carrier.rule_matched}</span>
                      )}
                    </p>
                  )}
                </div>
              </div>

              {order.is_cod && (
                <div className="mt-3 px-3 py-2 bg-orange-50 dark:bg-orange-900/20 border border-orange-200 dark:border-orange-800 rounded-lg">
                  <p className="text-sm font-medium text-orange-700 dark:text-orange-400">
                    Contrareembolso: €{order.cod_amount?.toFixed(2)}
                  </p>
                </div>
              )}
            </div>
          </div>

          {/* Columna 3: Pago + Almacén + Acciones */}
          <div className="w-72 p-5 bg-slate-50 dark:bg-slate-900/30">
            {/* Datos de Pago */}
            <div className="mb-6">
              <h3 className="text-xs font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wider mb-4">
                Datos de Pago
              </h3>

              <div className="space-y-3">
                <div className="flex items-center gap-3">
                  <FileText className="w-4 h-4 text-slate-400" />
                  <span className="text-sm text-slate-700 dark:text-slate-300">F{order.id}</span>
                </div>
                <div className="flex items-center gap-3">
                  <CreditCard className="w-4 h-4 text-slate-400" />
                  <span className="text-sm text-slate-700 dark:text-slate-300">{order.payment_method}</span>
                </div>
              </div>
            </div>

            {/* Almacén Asignado */}
            <div className="mb-6">
              <h3 className="text-xs font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wider mb-4">
                Almacén Asignado
              </h3>
              <div className="flex items-center gap-3">
                <Warehouse className="w-4 h-4 text-slate-400" />
                <span className="text-sm text-slate-700 dark:text-slate-300">Default Warehouse</span>
              </div>
            </div>

            {/* Acciones */}
            <div className="space-y-3">
              {/* Botón Editar datos: solo visible cuando NO está editando */}
              {!isEditing && (
                <button
                  onClick={() => setIsEditing(true)}
                  className="w-full flex items-center justify-center gap-2 px-4 py-2.5 text-slate-700 dark:text-slate-300 bg-white dark:bg-slate-700 border border-slate-300 dark:border-slate-600 hover:bg-slate-50 dark:hover:bg-slate-600 rounded-lg font-medium transition-colors"
                >
                  <Pencil className="w-4 h-4" />
                  Editar datos
                </button>
              )}

              {/* Aviso de pedido agrupado */}
              {order.is_grouped && order.grouped_with && (
                <div className="p-3 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg">
                  <div className="flex items-center gap-2 text-blue-700 dark:text-blue-400">
                    <Link2 className="w-4 h-4" />
                    <span className="text-sm font-medium">Pedido agrupado</span>
                  </div>
                  <p className="text-xs text-blue-600 dark:text-blue-300 mt-1">
                    Este pedido va en el mismo envío que <span className="font-semibold">{order.grouped_with.parent_order_reference}</span>. No requiere etiqueta propia.
                  </p>
                </div>
              )}

              {/* Botón principal: Empezar a empaquetar */}
              {onAddToQueue && !isEditing && !order.is_grouped && (
                <button
                  onClick={async () => {
                    setIsAddingToQueue(true)
                    try {
                      await onAddToQueue(order.id)
                    } finally {
                      setIsAddingToQueue(false)
                    }
                  }}
                  disabled={isAddingToQueue}
                  className="w-full flex items-center justify-center gap-2 px-4 py-3 bg-teal-600 hover:bg-teal-700 text-white rounded-lg font-medium shadow-sm disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                >
                  {isAddingToQueue ? (
                    <Loader2 className="w-5 h-5 animate-spin" />
                  ) : (
                    <Package className="w-5 h-5" />
                  )}
                  Empezar a empaquetar
                </button>
              )}

              {/* Botón Guardar: solo visible cuando está editando */}
              {isEditing && (
                <button
                  onClick={async () => {
                    await handleSave()
                    setIsEditing(false)
                  }}
                  disabled={isLoading || !hasChanges}
                  className="w-full flex items-center justify-center gap-2 px-4 py-3 bg-teal-600 hover:bg-teal-700 text-white rounded-lg font-medium shadow-sm disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                >
                  {isLoading ? (
                    <Loader2 className="w-5 h-5 animate-spin" />
                  ) : (
                    <Check className="w-5 h-5" />
                  )}
                  Guardar cambios
                </button>
              )}

              {/* Botón Cancelar edición o Cerrar */}
              <button
                onClick={() => {
                  if (isEditing) {
                    // Restaurar datos originales y salir de modo edición
                    setDeliveryData({
                      name: order.shipping_address.name,
                      company: order.shipping_address.company || '',
                      email: order.customer_email,
                      phone: order.shipping_address.phone_mobile || order.shipping_address.phone || '',
                      street: order.shipping_address.street,
                      street2: order.shipping_address.street2 || '',
                      city: order.shipping_address.city,
                      postalCode: order.shipping_address.postal_code,
                      country: order.shipping_address.country_code,
                      nif: order.dni || '',
                    })
                    setIsEditing(false)
                  } else {
                    onClose()
                  }
                }}
                className="w-full flex items-center justify-center gap-2 px-4 py-2.5 border border-slate-300 dark:border-slate-600 text-slate-600 dark:text-slate-400 rounded-lg text-sm hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors"
              >
                <X className="w-4 h-4" />
                {isEditing ? 'Cancelar edición' : 'Cerrar'}
              </button>
            </div>
          </div>
        </div>

        {/* Contenido del envío */}
        <div className="border-t border-slate-200 dark:border-slate-700 p-5">
          <h3 className="text-xs font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wider mb-4">
            Contenido del Envío
          </h3>

          <div className="flex gap-6">
            {/* Descripción y valor */}
            <div className="flex-1 flex gap-4">
              <div className="flex-1">
                <label className="text-[10px] text-slate-500 uppercase tracking-wider">Descripción Contenido</label>
                <input
                  type="text"
                  placeholder="Breve descripción del envío, en inglés si es internacional"
                                    className="w-full mt-1 px-3 py-2 text-sm rounded-lg border border-slate-200 dark:border-slate-600 bg-white dark:bg-slate-700 focus:ring-2 focus:ring-teal-500 focus:border-teal-500 placeholder:text-slate-400"
                />
              </div>
              <div className="w-32">
                <label className="text-[10px] text-slate-500 uppercase tracking-wider">Valor Total (EUR)</label>
                <input
                  type="text"
                  value={combinedProducts.reduce((sum, p) => sum + (p.price * p.quantity), 0).toFixed(2)}
                  disabled
                  className="w-full mt-1 px-3 py-2 text-sm rounded-lg border border-slate-200 dark:border-slate-600 bg-slate-50 dark:bg-slate-800 text-slate-700 dark:text-slate-300"
                />
              </div>
            </div>
          </div>

          {/* Productos + Precios */}
          <div className="flex gap-6 mt-4">
            {/* Lista de productos en grid horizontal */}
            <div className="flex-1">
              <div className="flex flex-wrap gap-3">
                {/* Productos existentes */}
                {combinedProducts.map((product, index) => (
                  <div key={`${product.isFromPrestaShop ? 'ps' : 'custom'}-${product.id}-${index}`} className="w-52 bg-slate-50 dark:bg-slate-900/50 rounded-xl border border-slate-200 dark:border-slate-700 overflow-hidden group relative">
                    {/* Badge de producto añadido manualmente */}
                    {!product.isFromPrestaShop && (
                      <div className="absolute top-1 left-1 px-1.5 py-0.5 bg-teal-500 text-white text-[9px] font-medium rounded z-10">
                        Añadido
                      </div>
                    )}

                    {/* Botón eliminar en esquina superior derecha */}
                    <button
                      onClick={() => {
                        if (product.isFromPrestaShop) {
                          markRemovedMutation.mutate(product.id)
                        } else if ('shipmentProductId' in product) {
                          deleteCustomProductMutation.mutate(product.shipmentProductId)
                        }
                      }}
                      disabled={markRemovedMutation.isPending || deleteCustomProductMutation.isPending}
                      className="absolute top-1 right-1 p-1 text-slate-400 hover:text-red-500 hover:bg-red-50 dark:hover:bg-red-900/20 rounded opacity-0 group-hover:opacity-100 transition-all z-10"
                      title="Excluir del envío"
                    >
                      <Trash2 className="w-3.5 h-3.5" />
                    </button>

                    {/* Imagen del producto */}
                    <div className="h-24 bg-white dark:bg-slate-700 flex items-center justify-center overflow-hidden">
                      {product.image_url ? (
                        <img
                          src={product.image_url}
                          alt={product.name}
                          className="w-full h-full object-contain p-2"
                          onError={(e) => {
                            e.currentTarget.src = ''
                            e.currentTarget.onerror = null
                            e.currentTarget.parentElement!.innerHTML = '<svg class="w-10 h-10 text-slate-300" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4"></path></svg>'
                          }}
                        />
                      ) : (
                        <Package className="w-10 h-10 text-slate-300 dark:text-slate-500" />
                      )}
                    </div>

                    {/* Info del producto */}
                    <div className="p-3 space-y-1.5">
                      {product.reference && (
                        <p className="text-[10px] text-slate-400 dark:text-slate-500 uppercase tracking-wider">
                          SKU: {product.reference}
                        </p>
                      )}

                      <div className="flex justify-between items-center">
                        <span className="text-[10px] text-slate-500">CANTIDAD</span>
                        <div className="flex items-center gap-1">
                          <button
                            onClick={() => {
                              if (product.quantity > 1) {
                                if (product.isFromPrestaShop) {
                                  updateQuantityMutation.mutate({ productId: product.id, quantity: product.quantity - 1 })
                                }
                              }
                            }}
                            disabled={product.quantity <= 1 || updateQuantityMutation.isPending}
                            className="w-5 h-5 flex items-center justify-center rounded bg-slate-200 dark:bg-slate-600 hover:bg-slate-300 dark:hover:bg-slate-500 text-slate-600 dark:text-slate-300 disabled:opacity-40 disabled:cursor-not-allowed transition-colors text-xs"
                          >
                            -
                          </button>
                          <span className="w-6 text-center text-xs font-medium text-slate-700 dark:text-slate-300">
                            {product.quantity}
                          </span>
                          <button
                            onClick={() => {
                              if (product.isFromPrestaShop) {
                                updateQuantityMutation.mutate({ productId: product.id, quantity: product.quantity + 1 })
                              }
                            }}
                            disabled={updateQuantityMutation.isPending}
                            className="w-5 h-5 flex items-center justify-center rounded bg-slate-200 dark:bg-slate-600 hover:bg-slate-300 dark:hover:bg-slate-500 text-slate-600 dark:text-slate-300 disabled:opacity-40 transition-colors text-xs"
                          >
                            +
                          </button>
                        </div>
                      </div>

                      <div className="flex justify-between items-center">
                        <span className="text-[10px] text-slate-500">PRECIO UNIDAD</span>
                        <span className="text-xs font-medium text-slate-700 dark:text-slate-300">
                          €{product.price.toFixed(2)}
                        </span>
                      </div>

                      {product.quantity > 1 && (
                        <div className="flex justify-between items-center pt-1 border-t border-slate-200 dark:border-slate-600">
                          <span className="text-[10px] text-slate-500">TOTAL</span>
                          <span className="text-xs font-semibold text-slate-900 dark:text-white">
                            €{(product.price * product.quantity).toFixed(2)}
                          </span>
                        </div>
                      )}
                    </div>
                  </div>
                ))}

                {/* Tarjeta para añadir producto (estilo Outvio) */}
                <button
                  onClick={() => setShowAddProductModal(true)}
                  className="w-52 h-[200px] bg-white dark:bg-slate-800 rounded-xl border-2 border-dashed border-slate-300 dark:border-slate-600 hover:border-teal-500 dark:hover:border-teal-500 hover:bg-teal-50/50 dark:hover:bg-teal-900/10 flex flex-col items-center justify-center gap-2 transition-colors group"
                >
                  <div className="w-10 h-10 rounded-full bg-slate-100 dark:bg-slate-700 flex items-center justify-center group-hover:bg-teal-100 dark:group-hover:bg-teal-900/30 transition-colors">
                    <Plus className="w-5 h-5 text-slate-400 group-hover:text-teal-600 dark:group-hover:text-teal-400 transition-colors" />
                  </div>
                  <span className="text-sm text-slate-400 group-hover:text-teal-600 dark:group-hover:text-teal-400 transition-colors">
                    Añadir producto
                  </span>
                </button>
              </div>

              {/* Productos eliminados (con opción de restaurar) */}
              {removedProducts.length > 0 && (
                <div className="mt-4 pt-4 border-t border-slate-200 dark:border-slate-700">
                  <p className="text-xs font-medium text-slate-500 dark:text-slate-400 mb-2">
                    Productos excluidos del envío:
                  </p>
                  <div className="flex flex-wrap gap-2">
                    {removedProducts.map((product) => (
                      <div key={`removed-${product.id}`} className="flex items-center gap-2 px-2 py-1.5 bg-red-50 dark:bg-red-900/10 rounded-lg border border-red-200 dark:border-red-800">
                        <div className="w-8 h-8 bg-white dark:bg-slate-700 rounded border border-slate-200 dark:border-slate-600 flex items-center justify-center overflow-hidden">
                          {product.image_url ? (
                            <img src={product.image_url} alt="" className="w-full h-full object-contain opacity-50" />
                          ) : (
                            <Package className="w-3 h-3 text-slate-300" />
                          )}
                        </div>
                        <span className="text-xs text-slate-500 dark:text-slate-400 line-through max-w-[100px] truncate">
                          {product.reference || product.name.substring(0, 15)}
                        </span>
                        <button
                          onClick={() => restoreProductMutation.mutate(product.id)}
                          disabled={restoreProductMutation.isPending}
                          className="p-1 text-teal-600 dark:text-teal-400 hover:bg-teal-50 dark:hover:bg-teal-900/20 rounded transition-colors"
                          title="Restaurar producto"
                        >
                          <RotateCcw className="w-3 h-3" />
                        </button>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>

            {/* Desglose de precios */}
            <div className="w-64 bg-slate-100 dark:bg-slate-800 rounded-xl p-4 space-y-3 h-fit">
              <div className="flex justify-between text-sm items-center">
                <span className="text-slate-500 dark:text-slate-400 text-xs font-medium">Productos</span>
                <span className="text-sm text-slate-700 dark:text-slate-300">
                  €{order.total_products?.toFixed(2) || combinedProducts.reduce((sum, p) => sum + (p.price * p.quantity), 0).toFixed(2)}
                </span>
              </div>
              <div className="flex justify-between text-sm items-center">
                <span className="text-slate-500 dark:text-slate-400 text-xs font-medium">Portes</span>
                <span className="text-sm text-slate-700 dark:text-slate-300">
                  €{order.total_shipping?.toFixed(2) || '0.00'}
                </span>
              </div>
              <div className="border-t border-slate-200 dark:border-slate-600 pt-3 flex justify-between items-center">
                <span className="font-semibold text-slate-700 dark:text-slate-300 text-xs">TOTAL PEDIDO</span>
                <span className="font-bold text-lg text-slate-900 dark:text-white">
                  €{order.total.toFixed(2)}
                </span>
              </div>
              <p className="text-[10px] text-slate-400 dark:text-slate-500 text-center">
                IVA incluido
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Modal para añadir producto */}
      {showAddProductModal && (
        <AddProductModal
          orderId={order.id}
          onClose={() => {
            setShowAddProductModal(false)
            setEditingProduct(null)
          }}
          onSuccess={() => {
            queryClient.invalidateQueries({ queryKey: ['shipment-products', order.id] })
            setShowAddProductModal(false)
            setEditingProduct(null)
          }}
          editingProduct={editingProduct}
        />
      )}
    </div>
  )
}
